import type { JSX, ReactNode } from 'react';
import type { CredentialExpiryFlag } from '@tsr/shared-types';

/**
 * Shared Operator-Mode portal primitives (Drone D1 #318). On-brand with the cold-boot
 * BootSplash / OperatorModeShell: dark ops-room slate.
 *
 * COLOUR DISCIPLINE (design-council Law 1): the COP reserves red + green as map-signal
 * hues. This is an admin surface, not the map, but the Operator shell deliberately stays
 * neutral — so urgency is signalled within the AMBER family (graduated intensity) plus an
 * explicit text label, never reserved red/green.
 */

/** A titled section card. */
export function PortalSection({
  title,
  testId,
  children,
}: {
  title: string;
  testId?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section
      data-testid={testId}
      className="flex w-full flex-col gap-3 rounded-lg border border-slate-800 bg-slate-900/60 p-5"
    >
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{title}</h2>
      {children}
    </section>
  );
}

export const inputClass =
  'rounded-md border border-slate-700 bg-slate-950/70 px-2.5 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-slate-500 focus:outline-none';

export const primaryButtonClass =
  'rounded-md bg-slate-200 px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-white disabled:opacity-40';

export const ghostButtonClass =
  'rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-100 disabled:opacity-40';

/** Field label + control stack. */
export function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const EXPIRY_BADGE: Record<CredentialExpiryFlag, { text: string; className: string }> = {
  none: { text: 'No expiry on file', className: 'text-slate-500 border-slate-700' },
  valid: { text: 'Current', className: 'text-slate-300 border-slate-600' },
  expiring: { text: 'Expiring soon', className: 'text-amber-300 border-amber-500/50 bg-amber-500/10' },
  expired: { text: 'Expired', className: 'text-amber-100 border-amber-400 bg-amber-900/40' },
};

/** Expiry status pill (amber-family by Law 1 — never reserved red/green). */
export function ExpiryBadge({ flag }: { flag: CredentialExpiryFlag }): JSX.Element {
  const b = EXPIRY_BADGE[flag];
  return (
    <span
      data-testid={`expiry-${flag}`}
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide border ${b.className}`}
    >
      {b.text}
    </span>
  );
}

/** Small neutral chip (sensors, tags). */
export function Chip({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-300">
      {children}
    </span>
  );
}
